'use client';

/**
 * AppShell - Main Layout Orchestrator
 *
 * Architecture:
 * - Universal sidebar for all staff pages (full-height, edge-to-edge)
 * - Sidebar collapsed for conference/fullscreen, expanded for others
 * - Footer lives inside the scrollable content area (not beside sidebar)
 * - NavBreadcrumb in header replaces traditional dropdown menus
 *
 * Layout Structure:
 * ┌─────────────────────────────────────────────────┐
 * │                    Header                        │
 * ├────────┬────────────────────────────────────────┤
 * │        │           Main Content                  │
 * │Sidebar │        (scrollable area)                │
 * │ (full  │                                         │
 * │ height)│────────────────────────────────────────│
 * │        │              Footer                     │
 * └────────┴────────────────────────────────────────┘
 */

import React from 'react';
import { useAppShell } from './AppShellContext';
import { UnifiedHeader } from './header/UnifiedHeader';
import type { HeaderVariant as UnifiedHeaderVariant } from './header/UnifiedHeader';
import { MainSidebar } from './navigation/MainSidebar';
import { AppFooter } from './footer/AppFooter';
import { FooterMinimal, FooterCitizen, FooterDeveloper } from './footer/FooterMinimal';
import { FooterClaimProvider, useIsFooterClaimed } from './footer/PageFooter';
import { AuthGuard } from './guards/AuthGuard';
import type { LayoutVariant, HeaderVariant, FooterVariant } from '@/lib/routes/types';

// ============================================================================
// Types
// ============================================================================

interface AppShellProps {
  children: React.ReactNode;
}

// ============================================================================
// Main Component
// ============================================================================

export function AppShell({ children }: AppShellProps) {
  const { currentRoute, layoutOverride, agencyBranding } = useAppShell();

  // Use override or route config
  const layout: LayoutVariant = layoutOverride || currentRoute?.layout || 'default';
  const header: HeaderVariant = currentRoute?.header || 'full';
  const footer: FooterVariant = currentRoute?.footer || 'hidden';
  const auth = currentRoute?.auth || 'none';

  // Wrap children in auth guard if needed
  const protectedChildren = auth !== 'none' ? (
    <AuthGuard requirement={auth}>
      {children}
    </AuthGuard>
  ) : (
    children
  );

  // Render based on layout variant
  switch (layout) {
    case 'fullscreen':
      return <FullscreenLayout>{protectedChildren}</FullscreenLayout>;

    case 'public':
      return <PublicLayout>{protectedChildren}</PublicLayout>;

    case 'auth':
      return (
        <AuthLayout branding={agencyBranding}>
          {protectedChildren}
        </AuthLayout>
      );

    case 'conference':
      return (
        <ConferenceLayout
          branding={agencyBranding}
        >
          {protectedChildren}
        </ConferenceLayout>
      );

    case 'citizen':
      return (
        <CitizenLayout branding={agencyBranding}>
          {protectedChildren}
        </CitizenLayout>
      );

    case 'developer':
      return (
        <DeveloperLayout branding={agencyBranding}>
          {protectedChildren}
        </DeveloperLayout>
      );

    case 'minimal':
      return (
        <MinimalLayout branding={agencyBranding}>
          {protectedChildren}
        </MinimalLayout>
      );

    case 'settings':
      return (
        <SettingsLayout
          branding={agencyBranding}
          header={header}
          footer={footer}
        >
          {protectedChildren}
        </SettingsLayout>
      );

    case 'admin':
    case 'dashboard':
    case 'default':
    default:
      return (
        <StandardLayout
          branding={agencyBranding}
          header={header}
          footer={footer}
        >
          {protectedChildren}
        </StandardLayout>
      );
  }
}

// ============================================================================
// Layout Components
// ============================================================================

interface LayoutProps {
  children: React.ReactNode;
  branding?: any; // AgencyBranding
}

/**
 * Fullscreen Layout - No chrome, just content
 */
function FullscreenLayout({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

/**
 * Public Layout - Pages that manage their own layout entirely
 */
function PublicLayout({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

/**
 * Auth Layout - Login, register, password reset pages
 */
function AuthLayout({ children, branding }: LayoutProps) {
  return (
    <div className="min-h-screen flex flex-col relative z-10" style={{ backgroundColor: '#000' }}>
      <UnifiedHeader variant="minimal" showBack backHref="/" showLogo={false} />
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        {children}
      </main>
    </div>
  );
}

/**
 * Conference Layout - Video call pages
 * Uses standard AppHeader for consistent menu/navigation
 * Full-height content area with no scrolling - pages manage their own layout
 */
function ConferenceLayout({ children, branding }: LayoutProps) {
  return (
    <div data-demo-id="conference-shell" className="flex flex-col relative z-10" style={{ backgroundColor: '#000', height: '100dvh' }}>
      {/* UnifiedHeader variant="conference" provides call-specific features: duration, recording indicator, call info, page actions */}
      <div className="flex-shrink-0 sticky top-0 z-50">
        <UnifiedHeader variant="conference" />
      </div>
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Sidebar - uses same MainSidebar as other layouts */}
        <MainSidebar />
        {/* Main content - full height, no padding, pages control their own layout */}
        <main data-demo-id="app-main-content" className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {children}
        </main>
      </div>
    </div>
  );
}

/**
 * Citizen Layout - Citizen portal pages (no sidebar)
 * Landing pages manage their own full-bleed layout, so no padding/max-width here
 */
function CitizenLayout({ children, branding }: LayoutProps) {
  return (
    <div className="min-h-screen flex flex-col relative z-10" style={{ backgroundColor: '#0a0a0a' }}>
      <UnifiedHeader variant="citizen" />
      <main>
        {children}
      </main>
      <FooterCitizen />
    </div>
  );
}

/**
 * Developer Layout - Developer portal pages (no sidebar, violet branding)
 * Landing pages manage their own full-bleed layout, so no padding/max-width here
 */
function DeveloperLayout({ children, branding }: LayoutProps) {
  return (
    <div className="min-h-screen flex flex-col relative z-10" style={{ backgroundColor: '#0a0a0a' }}>
      <UnifiedHeader variant="developer" />
      <main>
        {children}
      </main>
      <FooterDeveloper />
    </div>
  );
}

/**
 * Minimal Layout - Header and footer only, no sidebar
 */
function MinimalLayout({ children, branding }: LayoutProps) {
  return (
    <div className="min-h-screen flex flex-col relative z-10" style={{ backgroundColor: '#000' }}>
      <UnifiedHeader variant="minimal" />
      <main className="flex-1 px-4 py-6">
        {children}
      </main>
    </div>
  );
}

/**
 * Standard Layout - Main layout for dashboard, admin, settings, etc.
 * Features:
 * - Full-height sidebar (fills screen edge-to-edge)
 * - Footer inside scrollable content area
 * - Header with NavBreadcrumb
 */
interface StandardLayoutProps extends LayoutProps {
  header: HeaderVariant;
  footer: FooterVariant;
}

function StandardLayout({
  children,
  branding,
  header,
  footer,
}: StandardLayoutProps) {
  return (
    <div data-demo-id="app-shell" className="h-screen flex flex-col relative z-10" style={{ backgroundColor: '#000' }}>
      {/* Header */}
      {header !== 'hidden' && (
        <UnifiedHeader variant={header as UnifiedHeaderVariant} />
      )}

      {/* Main area with sidebar and content */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Sidebar - full height, attached to edge */}
        <MainSidebar />

        {/* Full-width content area - pages control their own layout */}
        <div className="flex-1 flex flex-col overflow-hidden min-h-0">
          {/* Main Content - fills available space, no width constraints */}
          <main data-demo-id="app-main-content" className="flex-1 flex flex-col min-h-0 overflow-auto">
            <FooterClaimProvider>
              {children}

              {/* Footer - inside scrollable area, scrolls with content.
                  Skipped when a child sub-menu layout claims it (renders footer in its own content column). */}
              <StandardFooter footer={footer} />
            </FooterClaimProvider>
          </main>
        </div>
      </div>
    </div>
  );
}

/** Renders footer only when no child layout has claimed it */
function StandardFooter({ footer }: { footer: FooterVariant }) {
  const claimed = useIsFooterClaimed();
  if (claimed || footer === 'hidden') return null;
  return footer === 'minimal' ? <FooterMinimal /> : <AppFooter />;
}

/**
 * Settings Layout - Full-width layout for settings pages
 * Features:
 * - Full-height sidebar (same as StandardLayout)
 * - NO max-w-7xl constraint - children control their own width
 * - Allows Settings sidebar to be flush against MainSidebar
 * - Footer hidden by default for settings
 */
function SettingsLayout({
  children,
  branding,
  header,
  footer,
}: StandardLayoutProps) {
  return (
    <div className="h-screen flex flex-col relative z-10" style={{ backgroundColor: '#000' }}>
      {/* Header */}
      {header !== 'hidden' && (
        <UnifiedHeader variant={header as UnifiedHeaderVariant} />
      )}

      {/* Main area with sidebar and FULL-WIDTH content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - full height, attached to edge */}
        <MainSidebar />

        {/* Full-width content area - NO max-w-7xl wrapper */}
        {/* Children (SettingsPageLayout) will handle their own internal layout */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <main className="flex-1 flex flex-col min-h-0 overflow-auto">
            <FooterClaimProvider>
              {children}
              <StandardFooter footer={footer} />
            </FooterClaimProvider>
          </main>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Export
// ============================================================================

export default AppShell;
